import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Calendar, Clock, User, Plus, Search, ArrowLeft } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ScheduleInterviewDialog } from "@/components/hr/ScheduleInterviewDialog";
import { FirstInterviewForm } from "@/components/hr/FirstInterviewForm";
import { SecondInterviewForm } from "@/components/hr/SecondInterviewForm";
import { useNavigate } from "react-router-dom";

interface Interview {
  id: string;
  candidate_id: string;
  interview_type: string;
  scheduled_date: string;
  status: string;
  candidates: {
    full_name: string;
    email: string;
    phone: string;
  };
}

export default function PendingInterviews() {
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [showInterviewForm, setShowInterviewForm] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState<Interview | null>(null);
  const [scheduleDialogProps, setScheduleDialogProps] = useState<{
    candidateId: string;
    candidateName: string;
    interviewType: 'first' | 'second';
  } | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPendingInterviews();
  }, []);

  const fetchPendingInterviews = async () => {
    try {
      const { data, error } = await supabase
        .from('interviews')
        .select(`
          *,
          candidates (
            full_name,
            email,
            phone
          )
        `)
        .eq('status', 'scheduled')
        .order('scheduled_date', { ascending: true });

      if (error) throw error;
      setInterviews(data || []);
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to fetch pending interviews",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredInterviews = interviews.filter(interview =>
    interview.candidates.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    interview.interview_type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getTypeColor = (type: string) => {
    return type === 'first' 
      ? 'bg-orange-100 text-orange-800' 
      : 'bg-purple-100 text-purple-800';
  };

  const handleStartInterview = (interview: Interview) => {
    setSelectedInterview(interview);
    setShowInterviewForm(true);
  };

  const handleInterviewComplete = (shouldScheduleSecond?: boolean) => {
    setShowInterviewForm(false);
    setSelectedInterview(null);
    fetchPendingInterviews(); // Refresh the list
    
    // If we should schedule second interview, open the scheduler
    if (shouldScheduleSecond && selectedInterview) {
      setShowScheduleDialog(true);
      setScheduleDialogProps({
        candidateId: selectedInterview.candidate_id,
        candidateName: selectedInterview.candidates.full_name,
        interviewType: 'second' as const
      });
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading pending interviews...</div>;
  }

  if (showInterviewForm && selectedInterview) {
    return selectedInterview.interview_type === 'first' ? (
      <FirstInterviewForm
        candidateId={selectedInterview.candidate_id}
        candidateName={selectedInterview.candidates.full_name}
        interviewId={selectedInterview.id}
        onComplete={handleInterviewComplete}
        onCancel={() => setShowInterviewForm(false)}
      />
    ) : (
      <SecondInterviewForm
        candidateId={selectedInterview.candidate_id}
        candidateName={selectedInterview.candidates.full_name}
        interviewId={selectedInterview.id}
        onComplete={handleInterviewComplete}
        onCancel={() => setShowInterviewForm(false)}
      />
    );
  }

  return (
    <div className="container mx-auto p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
        <Button variant="outline" onClick={() => navigate('/hr')} className="w-full sm:w-auto">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to HR Dashboard
        </Button>
        <h1 className="text-2xl sm:text-3xl font-bold">Pending Interviews</h1>
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="relative flex-1 w-full sm:max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search by candidate name or interview type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 w-full"
          />
        </div>
        <Button onClick={() => setShowScheduleDialog(true)} className="w-full sm:w-auto">
          <Plus className="h-4 w-4 mr-2" />
          <span className="hidden sm:inline">Schedule New Interview</span>
          <span className="sm:hidden">Schedule Interview</span>
        </Button>
      </div>

      <div className="grid gap-4">
        {filteredInterviews.map((interview) => (
          <Card key={interview.id} className="hover:shadow-md transition-shadow">
            <CardHeader>
              <div className="flex flex-col sm:flex-row justify-between items-start gap-3">
                <div className="flex-1">
                  <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                    <User className="h-4 w-4" />
                    {interview.candidates.full_name}
                  </CardTitle>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <Badge className={getTypeColor(interview.interview_type)}>
                      {interview.interview_type === 'first' ? 'First Interview' : 'Second Interview'}
                    </Badge>
                    <Badge variant="outline">
                      Scheduled
                    </Badge>
                  </div>
                </div>
                <div className="text-left sm:text-right w-full sm:w-auto">
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4 mr-1" />
                    {new Date(interview.scheduled_date).toLocaleDateString()}
                  </div>
                  <div className="flex items-center text-sm text-muted-foreground mt-1">
                    <Clock className="h-4 w-4 mr-1" />
                    {new Date(interview.scheduled_date).toLocaleTimeString([], { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="space-y-1 flex-1">
                  <p className="text-sm text-muted-foreground break-all">
                    Email: {interview.candidates.email || 'Not provided'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Phone: {interview.candidates.phone || 'Not provided'}
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleStartInterview(interview)}
                    className="w-full sm:w-auto"
                  >
                    Start Interview
                  </Button>
                  <Button variant="outline" size="sm" className="w-full sm:w-auto">
                    Reschedule
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredInterviews.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          {searchTerm ? 'No interviews match your search' : 'No pending interviews'}
        </div>
      )}

      <ScheduleInterviewDialog 
        open={showScheduleDialog} 
        onOpenChange={(open) => {
          setShowScheduleDialog(open);
          if (!open) setScheduleDialogProps(null);
        }}
        candidateId={scheduleDialogProps?.candidateId}
        candidateName={scheduleDialogProps?.candidateName}
        interviewType={scheduleDialogProps?.interviewType}
      />
    </div>
  );
}